import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import api from '../../services/api'
import toast from 'react-hot-toast'
import { ArrowRight } from 'lucide-react'

// ─── Curated creative imagery ──────────────────────────────────────────────
// A small set of SMA-relevant creative images representing different
// disciplines. Selection is deterministic per session so the student
// sees a different image each visit without a carousel or manual controls.
const CREATIVE_IMAGES = [
  { src: '/Gallery/Academy/potrait series/DR. CHRISTINE (4).jpg', label: 'Photography' },
  { src: '/Gallery/Academy/Learning In Action/971.jpg', label: 'Videography' },
  { src: '/Gallery/Academy/Learning In Action/23591868_musician_03.jpg', label: 'Audio Production' },
  { src: '/Gallery/Academy/Multimedia Workshops/DSC02424 copy.jpg', label: 'Graphic Design' },
  { src: '/Gallery/Academy/sma-studio-setup.jpg', label: 'Creative Technology' },
]

export default function Login() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const setAuth = useAuthStore((state) => state.setAuth)
  const [loaded, setLoaded] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)

  // Deterministic session-based image selection — rotates each visit
  const selectedImage = useMemo(() => {
    const sessionKey = sessionStorage.getItem('sma-login-image-index')
    let index
    if (sessionKey !== null) {
      index = (parseInt(sessionKey, 10) + 1) % CREATIVE_IMAGES.length
    } else {
      index = Math.floor(Math.random() * CREATIVE_IMAGES.length)
    }
    sessionStorage.setItem('sma-login-image-index', String(index))
    return CREATIVE_IMAGES[index]
  }, [])

  useEffect(() => {
    document.title = 'Sign In — Synergy Multimedia Academy'
    const metaDesc = document.querySelector('meta[name="description"]')
    if (metaDesc) metaDesc.setAttribute('content', 'Sign in to your SMA creative workspace. Access courses, projects, and mentorship.')
    const t = setTimeout(() => setLoaded(true), 200)
    return () => clearTimeout(t)
  }, [])

  const [formData, setFormData] = useState({
    identifier: '',
    password: '',
  })
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      const response = await api.post('/auth/login', {
        email: formData.identifier,
        password: formData.password,
      })
      const { user, token } = response.data

      setAuth(user, token)
      toast.success('Welcome back.')

      // If a redirect target was specified (e.g. from the kiosk startup page),
      // navigate there instead of the default dashboard. Only allow internal
      // paths to prevent open-redirect attacks.
      const redirect = searchParams.get('redirect')
      if (redirect && redirect.startsWith('/')) {
        navigate(redirect)
        return
      }

      switch (user.user_type) {
        case 'student':
          navigate('/student/dashboard')
          break
        case 'trainer':
          navigate('/trainer/dashboard')
          break
        case 'admin':
          navigate('/admin/dashboard')
          break
        case 'finance':
          navigate('/finance/dashboard')
          break
        case 'project_lab_mentor':
          navigate('/mentor/dashboard')
          break
        default:
          navigate('/')
      }
    } catch (error) {
      const errMsg = error.response?.data?.error || 'Sign in failed'
      const isPending = error.response?.data?.pending
      if (isPending) {
        toast(errMsg, { icon: '⏳', duration: 5000 })
      } else {
        toast.error(errMsg)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-cinema-black flex flex-col overflow-hidden">
      {/* ── Top bar — SMA Digital Lab identity ─────────────────────────── */}
      <div
        className={`relative z-20 flex items-center justify-between px-6 sm:px-10 lg:px-14 py-6 transition-all duration-1000 ${
          loaded ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
        }`}
      >
        <div className="flex items-center gap-4">
          <img src="/Logos/SMA LOGO  DARK MODE.png" alt="SMA" className="h-8 opacity-90" />
        </div>
        <div className="flex items-center gap-6 sm:gap-8">
          <Link
            to="/explore"
            className="group flex items-center gap-2 font-sans text-[0.65rem] uppercase tracking-[0.25em] text-cinema-muted hover:text-cinema-gold transition-colors"
          >
            Explore SMA
            <ArrowRight className="w-3 h-3 transition-transform duration-300 group-hover:translate-x-0.5" />
          </Link>
          <div className="hidden sm:block text-right border-l border-white/10 pl-8">
            <p className="font-sans text-[0.65rem] uppercase tracking-[0.25em] text-cinema-muted">
              SMA Digital Lab
            </p>
            <p className="font-sans text-[0.6rem] uppercase tracking-[0.2em] text-cinema-muted/60 mt-0.5">
              A Creative Workstation Environment
            </p>
          </div>
        </div>
      </div>

      {/* ── Main composition ───────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col lg:flex-row min-h-0">
        {/* Left — Creative visual (60%) */}
        <div className="hidden lg:flex lg:w-[60%] relative overflow-hidden">
          {/* Image layer */}
          <div
            className={`absolute inset-0 bg-cover bg-center transition-opacity duration-[2000ms] ease-out ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ backgroundImage: `url('${selectedImage.src}')` }}
          />
          {/* Dark gradient overlays for readability */}
          <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/50 to-black/90" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-transparent to-black/70" />
          <div className="absolute inset-0 bg-gradient-to-t from-cinema-black via-transparent to-black/40" />

          {/* Content */}
          <div className="relative z-10 flex flex-col justify-between p-12 xl:p-16 w-full">
            {/* Discipline label */}
            <div
              className={`transition-all duration-1000 delay-300 ${
                loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              <span className="cinema-label">{selectedImage.label}</span>
            </div>

            {/* Creative disciplines list */}
            <div
              className={`transition-all duration-1000 delay-500 ${
                loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              <div className="space-y-3">
                {CREATIVE_IMAGES.map((img) => (
                  <div
                    key={img.label}
                    className={`flex items-center gap-3 transition-opacity duration-500 ${
                      img.label === selectedImage.label ? 'opacity-100' : 'opacity-30'
                    }`}
                  >
                    <div
                      className={`h-px transition-all duration-500 ${
                        img.label === selectedImage.label ? 'w-12 bg-cinema-gold' : 'w-6 bg-cinema-muted/40'
                      }`}
                    />
                    <span
                      className={`font-sans text-sm tracking-wide transition-colors duration-500 ${
                        img.label === selectedImage.label ? 'text-cinema-soft' : 'text-cinema-muted'
                      }`}
                    >
                      {img.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Bottom — tagline */}
            <div
              className={`transition-all duration-1000 delay-700 ${
                loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              <p className="font-display text-lg text-cinema-soft/60 font-light italic">
                Where creative futures take shape.
              </p>
            </div>
          </div>

          {/* Hidden img to trigger onLoad for the background image */}
          <img
            src={selectedImage.src}
            alt=""
            className="hidden"
            onLoad={() => setImageLoaded(true)}
          />
        </div>

        {/* Right — Sign-in area (40%) */}
        <div className="flex-1 lg:w-[40%] flex items-center justify-center px-6 sm:px-10 lg:px-14 py-8 lg:py-12 relative">
          {/* Mobile image accent */}
          <div className="lg:hidden absolute top-0 left-0 right-0 h-48 overflow-hidden">
            <div
              className={`absolute inset-0 bg-cover bg-center transition-opacity duration-[2000ms] ${
                imageLoaded ? 'opacity-40' : 'opacity-0'
              }`}
              style={{ backgroundImage: `url('${selectedImage.src}')` }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-cinema-black" />
            <img
              src={selectedImage.src}
              alt=""
              className="hidden"
              onLoad={() => setImageLoaded(true)}
            />
          </div>

          <div className="relative z-10 w-full max-w-sm">
            {/* SMA identity */}
            <div
              className={`mb-12 transition-all duration-1000 delay-300 ${
                loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              <h1 className="font-display text-4xl text-white font-light tracking-tight">
                SMA
              </h1>
              <div className="h-px w-16 bg-cinema-gold/40 mt-4 mb-4" />
              <p className="font-sans text-xs uppercase tracking-[0.2em] text-cinema-muted mb-1">
                Synergy Multimedia Academy
              </p>
              <p className="font-sans text-xs uppercase tracking-[0.2em] text-cinema-gold/80">
                Shaping Futures.
              </p>
            </div>

            {/* Welcome */}
            <div
              className={`mb-10 transition-all duration-1000 delay-500 ${
                loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              <h2 className="font-display text-2xl text-white font-light">
                Welcome to SMA
              </h2>
              <p className="font-sans text-sm text-cinema-muted mt-2">
                Your creative workspace is ready.
              </p>
            </div>

            {/* Form — no card, just typography and hairline inputs */}
            <form
              onSubmit={handleSubmit}
              className="space-y-8"
              autoComplete="off"
            >
              <div
                className={`transition-all duration-700 delay-700 ${
                  loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
              >
                <label className="cinema-label block mb-3">Student ID / Email</label>
                <input
                  type="text"
                  className="w-full bg-transparent border-0 border-b border-white/10 text-white placeholder:text-cinema-muted/40 focus:border-cinema-gold focus:outline-none transition-colors py-2.5 px-0 font-sans font-light text-sm"
                  placeholder="you@email.com"
                  value={formData.identifier}
                  onChange={(e) => setFormData({ ...formData, identifier: e.target.value })}
                  required
                  disabled={loading}
                  autoComplete="off"
                />
              </div>

              <div
                className={`transition-all duration-700 delay-[900ms] ${
                  loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
              >
                <label className="cinema-label block mb-3">Password</label>
                <input
                  type="password"
                  className="w-full bg-transparent border-0 border-b border-white/10 text-white placeholder:text-cinema-muted/40 focus:border-cinema-gold focus:outline-none transition-colors py-2.5 px-0 font-sans font-light text-sm"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  disabled={loading}
                  autoComplete="off"
                />
              </div>

              <div
                className={`transition-all duration-700 delay-[1100ms] ${
                  loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
              >
                <button
                  type="submit"
                  className="w-full cinema-btn-primary flex items-center justify-center gap-2"
                  disabled={loading}
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </form>

            {/* Secondary links */}
            <div
              className={`mt-10 space-y-4 text-center transition-all duration-700 delay-[1300ms] ${
                loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              <Link
                to="/reset-password"
                className="block text-sm text-cinema-muted hover:text-cinema-gold transition-colors font-sans font-light"
              >
                Forgot password?
              </Link>

              <div className="cinema-divider" />

              <p className="text-sm text-cinema-muted font-sans font-light">
                New to SMA?{' '}
                <Link to="/register" className="text-cinema-gold hover:text-cinema-glow transition-colors font-medium">
                  Apply now
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Bottom bar ─────────────────────────────────────────────────── */}
      <div
        className={`relative z-10 flex items-center justify-between px-6 sm:px-10 lg:px-14 py-5 border-t border-white/5 transition-all duration-1000 delay-[1500ms] ${
          loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
        }`}
      >
        <p className="font-sans text-[0.65rem] uppercase tracking-[0.2em] text-cinema-muted/60">
          Synergy Multimedia Academy
        </p>
        <p className="font-sans text-[0.65rem] uppercase tracking-[0.2em] text-cinema-muted/60">
          Shaping Futures.
        </p>
      </div>
    </div>
  )
}
